import { type PlanMutationPayload } from '@/features/shop/services/DeliveryPlans/types'
import { apiPost } from '@/features/shared/utils/dataFetching'
import {
  PlanInterval,
  SubscriptionPlanType,
  type AuxMembershipPlan,
  type AuxPurchasePlan,
} from '@/features/account/services/SubscriptionAux/types'
import {
  DEFAULT_ANNUAL_COST,
  DEFAULT_MONTHLY_COST,
  DEFAULT_MONTHLY_SNAP_EBT_COST,
  DEFAULT_MONTHLY_STUDENT_COST,
  DEFAULT_PPO_FEE,
} from '@/features/account/services/SubscriptionAux/constants'

export const subscriptionAuxPurchase = async (
  package_id: PlanMutationPayload['package_id'],
  credit_card_id: PlanMutationPayload['credit_card_id'],
) =>
  apiPost<AuxPurchasePlan>({
    config: {
      url: `subscription-auxiliary/v1/plans/${package_id}`,
      data: { credit_card_id },
    },
    options: {
      ignoredMessages: ['ineligible for membership upgrade', 'Payment card was declined.'],
    },
    fetcherName: 'fetchSubscriptionAuxPurchase',
  })

export const getAnnualPlan = (plans: AuxMembershipPlan[]) => {
  const annualPlan =
    plans.find(
      (p) => p.interval === PlanInterval.ANNUAL && p.plan_type === SubscriptionPlanType.STANDARD,
    ) ?? null
  const annualCost = annualPlan
    ? Number((annualPlan.total_cost_cents / 100).toFixed(0))
    : DEFAULT_ANNUAL_COST
  return { annualPlan, annualCost }
}

export const getMonthlyPlan = (plans: AuxMembershipPlan[]) => {
  const monthlyPlan =
    plans.find(
      (p) => p.interval === PlanInterval.MONTHLY && p.plan_type === SubscriptionPlanType.STANDARD,
    ) ?? null
  const monthlyCost = monthlyPlan
    ? Number((monthlyPlan.total_cost_cents / 100).toFixed(2))
    : DEFAULT_MONTHLY_COST
  return { monthlyPlan, monthlyCost }
}

export const getStudentPlan = (plans: AuxMembershipPlan[]) => {
  const studentPlan = plans.find((p) => p.plan_type === 'student') ?? null
  const monthlyCost = studentPlan
    ? Number((studentPlan.total_cost_cents / 100).toFixed(2))
    : DEFAULT_MONTHLY_STUDENT_COST
  return { studentPlan, monthlyCost }
}

export const getSnapEBTPlan = (plans: AuxMembershipPlan[]) => {
  const snapEBTPlan = plans.find((p) => p.plan_type === 'ebt') ?? null
  const monthlyCost = snapEBTPlan
    ? Number((snapEBTPlan.total_cost_cents / 100).toFixed(2))
    : DEFAULT_MONTHLY_SNAP_EBT_COST
  return { snapEBTPlan, monthlyCost }
}

export const getEarlyRenewalPlan = (plans: AuxMembershipPlan[]) => {
  const earlyRenewalPlan =
    plans.find((p) => p.plan_type === SubscriptionPlanType.EARLY_RENEWAL) ?? null
  return { earlyRenewalPlan }
}

export const getTrialEligible = (plans: AuxMembershipPlan[], planInterval: PlanInterval) => {
  const plan = plans.find(
    (p) => p.interval === planInterval && p.plan_type === SubscriptionPlanType.STANDARD,
  )
  const trialPeriodDays = plan?.trial_period_days ?? 0

  return {
    isTrialEligible: trialPeriodDays > 0,
    trialEligibleDays: trialPeriodDays,
  }
}

export const getSingleDeliveryFee = (plan: AuxMembershipPlan[] | undefined) => {
  const feeInCents = plan?.[0]?.single_delivery_fee_cents ?? DEFAULT_PPO_FEE
  const singleDeliveryFee = feeInCents / 100
  return { singleDeliveryFee }
}
